<script setup lang="ts">
import { ElDropdown, ElDropdownItem, ElDropdownMenu, ElIcon } from 'element-plus'
import { computed, onUnmounted, ref, watch } from 'vue'

import { BoxComponent, ButtonComponent, FlexComponent, InputComponent, TypographyComponent } from '../..'
import CheckIcon from '../../../assets/icons/check.svg?component'
import ChevronIcon from '../../../assets/icons/chevron.svg?component'
import RefreshIcon from '../../../assets/icons/refresh.svg?component'
import { debounce } from '../../../utils'

type TagOption = { id: number; name: string }

const props = withDefaults(
  defineProps<{
    options: TagOption[]
    placeholder?: string
    allLabel?: string
    loading?: boolean
    width?: string | number
    serverSearch?: boolean
  }>(),
  {
    placeholder: 'Search tags',
    allLabel: 'All tags',
    loading: false,
    width: 240,
    serverSearch: false,
  },
)
const selected = defineModel<number[]>({ default: () => [] })
const emit = defineEmits<{ search: [query: string] }>()

const searchQuery = ref('')
const debouncedServerSearch = debounce((query: string) => emit('search', query), 300)
const knownOptions = ref<TagOption[]>([])
watch(
  () => [props.options, selected.value] as const,
  ([options, selectedIds]) => {
    const previousSelectedOptions = knownOptions.value.filter(
      (option) => selectedIds.includes(option.id) && !options.some(({ id }) => id === option.id),
    )
    knownOptions.value = [...options, ...previousSelectedOptions]
  },
  { immediate: true, deep: true },
)

const filteredOptions = computed(() => {
  if (props.serverSearch) {
    return props.options
  }
  const query = searchQuery.value.trim().toLowerCase()
  if (!query) {
    return props.options
  }
  return props.options.filter((option) => option.name.toLowerCase().includes(query))
})

const displayLabel = computed(() => {
  if (!selected.value.length) {
    return props.allLabel
  }
  const names = selected.value
    .map((id) => knownOptions.value.find((option) => option.id === id)?.name)
    .filter((name): name is string => Boolean(name))
  if (!names.length) {
    return props.allLabel
  }
  return names.length > 1 ? `${names[0]} +${names.length - 1}` : names[0]
})

const isSelected = (id: number) => selected.value.includes(id)

const toggle = (id: number) => {
  selected.value = isSelected(id) ? selected.value.filter((selectedId) => selectedId !== id) : [...selected.value, id]
}

const handleSearchInput = (value: string) => {
  searchQuery.value = value
  if (props.serverSearch) {
    debouncedServerSearch(value)
  }
}

const clear = () => {
  selected.value = []
}

const handleVisible = (visible: boolean) => {
  if (!visible) {
    debouncedServerSearch.cancel()
    searchQuery.value = ''
    if (props.serverSearch) {
      emit('search', '')
    }
  }
}

onUnmounted(() => debouncedServerSearch.cancel())
</script>
<template>
  <ElDropdown trigger="click" :hide-on-click="false" @visible-change="handleVisible">
    <BoxComponent role="button" tabindex="0" :p="10" :title="displayLabel" class="tag-selector__label">
      <FlexComponent :gap="4" align="center">
        <TypographyComponent variant="label-large">{{ displayLabel }}</TypographyComponent>
        <ElIcon :size="18">
          <ChevronIcon />
        </ElIcon>
      </FlexComponent>
    </BoxComponent>
    <template #dropdown>
      <BoxComponent :pt="8" :pb="8">
        <BoxComponent :mb="8" :pr="16" :pl="16">
          <InputComponent size="small" :placeholder="placeholder" :model-value="searchQuery" @input="handleSearchInput" />
        </BoxComponent>
        <BoxComponent v-loading="loading" :min-w="120" :min-h="35" :w="width">
          <ElDropdownMenu v-if="filteredOptions.length > 0" size="large">
            <ElDropdownItem v-for="option in filteredOptions" :key="option.id" @click="toggle(option.id)">
              <BoxComponent :p="16" w="100%" :title="option.name">
                <FlexComponent flex="1" justify="space-between" align="center" :gap="8">
                  <TypographyComponent :variant="isSelected(option.id) ? 'title-large' : 'body-large'">
                    {{ option.name }}
                  </TypographyComponent>
                  <ElIcon v-if="isSelected(option.id)" :size="24">
                    <CheckIcon />
                  </ElIcon>
                </FlexComponent>
              </BoxComponent>
            </ElDropdownItem>
          </ElDropdownMenu>
          <BoxComponent v-if="!loading && filteredOptions.length === 0" :pt="10" :pb="10">
            <TypographyComponent is="p" variant="body-small" text-align="center">No matching tags</TypographyComponent>
          </BoxComponent>
        </BoxComponent>
        <ButtonComponent variant="text" class="tag-selector__clear-button" @click="clear">
          <template #iconLeft>
            <ElIcon :size="18">
              <RefreshIcon />
            </ElIcon>
          </template>
          Clear filter
        </ButtonComponent>
      </BoxComponent>
    </template>
  </ElDropdown>
</template>
<style lang="scss" scoped>
.tag-selector__label {
  cursor: pointer;
  border-radius: 64px;
  &:hover {
    background: var(--button-hover);
  }
}
:deep(.el-dropdown-menu) {
  max-height: 440px;
  overflow: auto;
}
:deep(.el-dropdown-menu__item) {
  padding: 0;
  white-space: normal;
}
:deep(.el-input--small .el-input__wrapper) {
  padding: 8px;
}
.button.tag-selector__clear-button {
  width: 100%;
  border-radius: 0 0 16px 16px;
  padding: 16px;
  margin-bottom: -8px;
  height: 56px;
  border-top: 1px solid var(--button-hover);
  justify-content: flex-start;
  font-weight: 600;
}
</style>
